import { HStack, Text, Tooltip, VStack } from '@chakra-ui/react';
import { CrowdStrikeMark, MsDefenderMark, PaloAltoMark } from '@gamma/ui/logos';
import { EDRIntegration } from '@investigator/features/integrations/pages/IntegrationsConstants';
import { MISSING_DATA_PLACEHOLDER } from '@investigator/shared/constants';
import { EdrHost } from '../../types';
import { EDR_SOURCE } from '../../utils/formatEntityFields';

// Max characters before truncating a single value
const VALUE_TRUNCATE_LENGTH = 20;

/**
 * Renders the appropriate EDR integration icon based on source
 */
const EDRIcon = ({ src }: { src: EDRIntegration }): JSX.Element | null => {
  switch (src) {
    case EDR_SOURCE.CROWDSTRIKE:
      return <CrowdStrikeMark boxSize={4} />;
    case EDR_SOURCE.MICROSOFT_DEFENDER:
      return <MsDefenderMark boxSize={4} />;
    case EDR_SOURCE.PALO_ALTO:
      return <PaloAltoMark boxSize={4} />;
    default:
      return null;
  }
};

interface SingleValueFieldProps {
  value?: string;
  fallback?: string;
  maxLength?: number;
}

/**
 * Renders a single value with truncation if too long.
 * Shows truncated text with tooltip for full value on hover.
 */
export const SingleValueField = ({
  value,
  fallback = MISSING_DATA_PLACEHOLDER,
  maxLength = VALUE_TRUNCATE_LENGTH,
}: SingleValueFieldProps): JSX.Element => {
  if (!value) {
    return (
      <Text textStyle="body-md" color="text.primary" whiteSpace="nowrap">
        {fallback}
      </Text>
    );
  }

  if (value.length <= maxLength) {
    return (
      <Text textStyle="body-md" color="text.primary" whiteSpace="nowrap">
        {value}
      </Text>
    );
  }

  const truncated = value.slice(0, maxLength) + '...';

  return (
    <Tooltip label={value} placement="top" hasArrow>
      <Text
        textStyle="body-md"
        color="text.primary"
        whiteSpace="nowrap"
        cursor="pointer"
      >
        {truncated}
      </Text>
    </Tooltip>
  );
};

/** Internal normalized item structure */
interface NormalizedEDRHost {
  value: string;
  source?: EDRIntegration;
}

interface MultiValueFieldProps {
  values?: string[];
  edrHosts?: EdrHost[];
  fallback?: string;
}

/**
 * Renders a field value that may contain multiple items.
 * Shows first item + "+N" indicator with tooltip for remaining items.
 * Optionally displays EDR source icons when edrHost is provided.
 */
export const MultiValueField = ({
  values,
  edrHosts,
  fallback = MISSING_DATA_PLACEHOLDER,
}: MultiValueFieldProps): JSX.Element => {
  // Normalize to common structure - edrHosts takes precedence
  const items: NormalizedEDRHost[] | undefined = edrHosts
    ? edrHosts
    : values?.map((v) => ({ value: v }));

  if (!items || items.length === 0) {
    return (
      <Text textStyle="body-md" color="text.primary" whiteSpace="nowrap">
        {fallback}
      </Text>
    );
  }

  const [first, ...rest] = items;
  const remainingCount = rest.length;
  const firstSource = first.source;

  // Truncate first value if too long
  const isTruncated = first.value.length > VALUE_TRUNCATE_LENGTH;
  const displayFirst = isTruncated
    ? first.value.slice(0, VALUE_TRUNCATE_LENGTH) + '...'
    : first.value;

  // Single value case
  if (remainingCount === 0) {
    if (firstSource) {
      // Single value with EDR icon
      const content = (
        <HStack spacing={1} cursor={isTruncated ? 'pointer' : undefined}>
          <EDRIcon src={firstSource} />
          <Text textStyle="body-md" color="text.primary" whiteSpace="nowrap">
            {displayFirst}
          </Text>
        </HStack>
      );

      return isTruncated ? (
        <Tooltip label={first.value} placement="top" hasArrow>
          {content}
        </Tooltip>
      ) : (
        content
      );
    }
    // Single value without source - use TruncatedValue for full tooltip support
    return <SingleValueField value={first.value} />;
  }

  // Multiple values case
  return (
    <HStack spacing={1}>
      {firstSource && <EDRIcon src={firstSource} />}
      <Text textStyle="body-md" color="text.primary" whiteSpace="nowrap">
        {displayFirst}
      </Text>
      <Tooltip
        label={
          <VStack align="start" spacing={firstSource ? 1 : 0}>
            {items.map((item, idx) =>
              item.source ? (
                <HStack key={idx} spacing={1}>
                  <EDRIcon src={item.source} />
                  <Text textStyle="body-md">{item.value}</Text>
                </HStack>
              ) : (
                <Text key={idx} textStyle="body-md">
                  {item.value}
                </Text>
              ),
            )}
          </VStack>
        }
        placement="top"
        hasArrow
      >
        <Text
          textStyle="body-md"
          color="blue.400"
          cursor="pointer"
          whiteSpace="nowrap"
          _hover={{ textDecoration: 'underline' }}
        >
          +{remainingCount}
        </Text>
      </Tooltip>
    </HStack>
  );
};
